import React, { FC, useMemo, useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  MenuItem,
  CircularProgress,
  TablePagination,
} from '@material-ui/core';
import { Search, GetApp, Person, Group, Domain, BusinessCenter, Remove, Sync } from '@material-ui/icons';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { handleCompanyUrl } from 'utils/organizationUtils';

interface TimelineProps {
  events?: any;
  loading?: boolean;
  userName?: string;
  userEmail?: string;
  header?: string;
}

type TimelineEvent = Record<string, any>;
const getDateValue = (event: TimelineEvent): Date | null => {
  const raw = event.created_at || event.createdAt || event.createtimestamp || event.timestamp;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};
const text = (value: any): string => (value == null ? '' : String(value));
const getActor = (e: TimelineEvent): string => {
  const direct = e.actor_name || e.created_by_name || e.admin_name || e.actor || e.created_by_email;
  if (direct) return text(direct);
  const description = text(e.description || e.raw_description);
  const match = description.match(/\sby\s+([^\n.]+)(?:\.|$)/i);
  return match ? match[1].trim() : '';
};
const getCategory = (e: TimelineEvent): string => {
  const content = `${text(e.title)} ${text(e.type)} ${text(e.description)} ${text(e.raw_description)}`.toLowerCase();
  if (/sync|synchroniz/.test(content)) return 'sync';
  if (/department/.test(content)) return 'department';
  if (/group/.test(content)) return 'group';
  if (/app|application/.test(content)) return 'app';
  return 'user';
};
const getAction = (e: TimelineEvent): string => {
  const content = `${text(e.title)} ${text(e.type)} ${text(e.raw_description)}`.toLowerCase();
  if (/remov|unassign|revok|delet/.test(content)) return 'removed';
  if (/assign|add|grant|provision/.test(content)) return 'assigned';
  if (/sync/.test(content)) return 'sync';
  return 'activity';
};
const getTitle = (e: TimelineEvent): string => {
  if (e.title) return text(e.title);
  const category = getCategory(e);
  const action = getAction(e);
  if (action === 'sync') return 'User synced with application';
  return `${category.charAt(0).toUpperCase()}${category.slice(1)} ${action === 'activity' ? 'updated' : action}`;
};
const dateGroup = (d: Date | null) =>
  d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'Earlier activity';
const dateTime = (d: Date | null) => (d ? d.toLocaleString() : 'Unknown date');
const timeOnly = (d: Date | null) =>
  d ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

const AppTimeline: FC<TimelineProps> = ({ events = [], loading = false, userName, header }) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    ldapStore: { viewTimelines, viewGoogleTimelineUser, viewTimelineAdmin },
    notificationStore: { openNotification },
  } = useStore();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const profile = (() => {
    try {
      return JSON.parse(localStorage.getItem('Profile') || 'null');
    } catch {
      return null;
    }
  })();

  const { timelineEvents, displayName } = useMemo(() => {
    const timelineEvents: TimelineEvent[] = Array.isArray(events)
      ? events
      : Array.isArray(events?.timeline?.data)
      ? events.timeline.data
      : [];
    return { timelineEvents, displayName: userName || events?.app_name || events?.name || 'app' };
  }, [events, userName]);

  const getClickedUser = useMutation((id: number) => viewGoogleTimelineUser(id, header), {
    onSuccess: (result: any) => {
      const message = result?.data?.message;
      if (result?.data?.user) localStorage.setItem('ClickedUser', JSON.stringify(result.data.user));
      const path =
        message === 'User found'
          ? '/user/view'
          : message === 'Group found'
          ? '/group/users'
          : message === 'Department found'
          ? '/department/users'
          : '/admin/profile';
      window.open(handleCompanyUrl(path), '_blank');
    },
    onError: (err: any) => openNotification('error', err?.data?.message || err?.message || 'User not found'),
  });
  const getClicked = useMutation(
    ({ clientId, entityType }: { clientId: number; entityType: 'user' | 'group' | 'department' }) =>
      viewTimelines(clientId, entityType),
    {
      onSuccess: (result: any) => {
        if (result?.data?.user) localStorage.setItem('ClickedUser', JSON.stringify(result.data.user));
        const message = result?.data?.message;
        const path =
          message === 'User found'
            ? '/user/view'
            : message === 'Group found'
            ? '/group/users'
            : message === 'Department found'
            ? '/department/users'
            : '/admin/profile';
        window.open(handleCompanyUrl(path), '_blank');
      },
      onError: (err: any) => openNotification('error', err?.data?.message || err?.message || 'Record not found'),
    }
  );
  const getClickedAdmin = useMutation((id: number) => viewTimelineAdmin(id), {
    onSuccess: () => {
      window.open(handleCompanyUrl('/admin/profile'), '_blank');
    },
    onError: (err: any) => openNotification('error', err?.data?.message || err?.message || 'Admin not found'),
  });

  const actors = useMemo(
    () => Array.from(new Set(timelineEvents.map(getActor).filter(Boolean))).sort(),
    [timelineEvents]
  );
  const dates = useMemo(
    () =>
      Array.from(
        new Set(
          timelineEvents
            .map((e) => {
              const d = getDateValue(e);
              return d
                ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
                    2,
                    '0'
                  )}`
                : '';
            })
            .filter(Boolean)
        )
      )
        .sort()
        .reverse(),
    [timelineEvents]
  );

  const filteredEvents = useMemo(
    () =>
      timelineEvents
        .filter((e) => {
          const q = search.trim().toLowerCase();
          const searchable = [
            e.title,
            e.description,
            e.raw_description,
            e.type,
            getActor(e),
            e.created_at,
            e.createdAt,
            e.createtimestamp,
          ]
            .map(text)
            .join(' ')
            .toLowerCase();
          const d = getDateValue(e);
          const day = d
            ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            : '';
          return (
            (!q || searchable.includes(q)) &&
            (typeFilter === 'all' || getCategory(e) === typeFilter) &&
            (dateFilter === 'all' || day === dateFilter) &&
            (actorFilter === 'all' || getActor(e) === actorFilter)
          );
        })
        .sort((a, b) => (getDateValue(b)?.getTime() || 0) - (getDateValue(a)?.getTime() || 0)),
    [timelineEvents, search, typeFilter, dateFilter, actorFilter]
  );

  useEffect(() => {
    setPage(0);
  }, [search, typeFilter, dateFilter, actorFilter, rowsPerPage]);
  const visible = filteredEvents.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const exportExcel = () => {
    const rows = filteredEvents.map((e) => ({
      Title: getTitle(e),
      Type: text(e.type || getCategory(e)),
      Action: getAction(e),
      Description: text(e.raw_description || e.description),
      Actor: getActor(e),
      Date: dateTime(getDateValue(e)),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timeline');
    XLSX.writeFile(wb, `timeline_${text(displayName).replace(/[^a-z0-9_-]/gi, '_')}.xlsx`);
  };
  const iconFor = (e: TimelineEvent) => {
    if (getAction(e) === 'removed') return <Remove style={{ fontSize: 16 }} />;
    const category = getCategory(e);
    if (category === 'sync') return <Sync style={{ fontSize: 16 }} />;
    if (category === 'group') return <Group style={{ fontSize: 16 }} />;
    if (category === 'department') return <Domain style={{ fontSize: 16 }} />;
    if (category === 'app') return <BusinessCenter style={{ fontSize: 16 }} />;
    return <Person style={{ fontSize: 16 }} />;
  };
  const openEntity = (e: TimelineEvent) => {
    const category = getCategory(e);
    const id =
      e.entity_id || (category === 'group' ? e.group_id : category === 'department' ? e.department_id : e.user_id);
    if (!id) return;
    if (category === 'group' || category === 'department')
      getClicked.mutate({ clientId: Number(id), entityType: category });
    else getClickedUser.mutate(Number(id));
  };
  const openActor = (e: TimelineEvent) => {
    const id = e.actor_id || e.created_by_id || e.admin_id || e.updated_by;
    if (id && profile?.permission !== 'Manager') getClickedAdmin.mutate(Number(id));
  };
  const appRoutes: Record<string, string> = {
    google: '/apps/google',
    zoom: '/apps/zoom',
    microsoft: '/apps/microsoft',
    github: '/apps/github',
  };
  const renderDescription = (e: TimelineEvent) => {
    const description = text(e.description || e.raw_description);
    if (!description) return null;
    // Preserve useful entity navigation without guessing entity IDs from free text.
    // const category = getCategory(e);
    const id = e.entity_id || e.user_id || e.group_id || e.department_id;
    const appPattern = /\b(Google|Zoom|Microsoft|GitHub)\b/gi;
    const pieces = description.split(appPattern);
    return (
      <span>
        {pieces.map((piece, i) => {
          const key = piece.toLowerCase();
          if (appRoutes[key])
            return (
              <span key={i} className={classes.links} onClick={() => history.push(handleCompanyUrl(appRoutes[key]))}>
                {piece}
              </span>
            );
          // Entity names in parentheses are clickable when an entity ID is present.
          const chunks = piece.split(/(\([^()]+\))/g);
          return (
            <React.Fragment key={i}>
              {chunks.map((chunk, j) =>
                /^\([^()]+\)$/.test(chunk) && id ? (
                  <span key={j} className={classes.links} onClick={() => openEntity(e)}>
                    {chunk}
                  </span>
                ) : (
                  <React.Fragment key={j}>{chunk}</React.Fragment>
                )
              )}
            </React.Fragment>
          );
        })}
      </span>
    );
  };

  let previousDay = '';
  return (
    <Paper className={classes.papers}>
      <div className={classes.header}>
        <div className={classes.heading}>
          <Typography className={classes.title}>Timeline</Typography>
          <span className={classes.count}>{timelineEvents.length} events</span>
        </div>
        <Button
          variant='contained'
          size='small'
          className={classes.exportButton}
          startIcon={<GetApp />}
          onClick={exportExcel}
          disabled={!filteredEvents.length}
        >
          EXPORT
        </Button>
      </div>
      <div className={classes.filters}>
        <TextField
          className={classes.filterField}
          size='small'
          variant='outlined'
          placeholder='Search timeline...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          className={classes.filterField}
          size='small'
          variant='outlined'
          select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <MenuItem value='all'>All event types</MenuItem>
          <MenuItem value='user'>Users</MenuItem>
          <MenuItem value='group'>Groups</MenuItem>
          <MenuItem value='department'>Departments</MenuItem>
          <MenuItem value='app'>Apps</MenuItem>
          <MenuItem value='sync'>Sync</MenuItem>
        </TextField>
        <TextField
          className={classes.filterField}
          size='small'
          variant='outlined'
          select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
        >
          <MenuItem value='all'>All dates</MenuItem>
          {dates.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className={classes.filterField}
          size='small'
          variant='outlined'
          select
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
        >
          <MenuItem value='all'>All actors</MenuItem>
          {actors.map((actor) => (
            <MenuItem key={actor} value={actor}>
              {actor}
            </MenuItem>
          ))}
        </TextField>
      </div>
      <div className={classes.meta}>
        <span>Showing {filteredEvents.length} events · Newest first</span>
        <span>Times in account timezone</span>
      </div>
      {loading ? (
        <div className={classes.emptyState}>
          <CircularProgress size={32} />
        </div>
      ) : !visible.length ? (
        <div className={classes.emptyState}>No timeline events found</div>
      ) : (
        visible.map((e, index) => {
          const day = dateGroup(getDateValue(e));
          const showDay = day !== previousDay;
          previousDay = day;
          const id = `${text(e.id || 'event')}-${page * rowsPerPage + index}`;
          const actor = getActor(e);
          const action = getAction(e);
          const entityName = text(e.entity_name || e.group_name || e.department_name || e.user_name);
          return (
            <React.Fragment key={id}>
              {showDay && <div className={classes.day}>{day}</div>}
              <div className={classes.event}>
                <div className={classes.iconColumn}>
                  {index !== visible.length - 1 && <div className={classes.line} />}
                  <span className={`${classes.icon} ${action === 'removed' ? classes.red : classes.blue}`}>
                    {iconFor(e)}
                  </span>
                </div>
                <div className={classes.body}>
                  <div className={classes.eventTitle}>{getTitle(e)}</div>
                  {entityName && (
                    <div className={classes.descriptions}>
                      <span className={classes.links} onClick={() => openEntity(e)}>
                        {entityName}
                      </span>
                    </div>
                  )}
                  {!entityName && <div className={classes.descriptions}>{renderDescription(e)}</div>}
                  <div className={classes.byline}>
                    {actor ? (
                      <>
                        By{' '}
                        <span
                          className={profile?.permission === 'Manager' ? '' : classes.links}
                          onClick={() => openActor(e)}
                        >
                          {actor}
                        </span>{' '}
                        ·{' '}
                      </>
                    ) : null}
                    {timeOnly(getDateValue(e))}
                  </div>
                  {expanded[id] && (
                    <div className={classes.detailBox}>
                      <div className={classes.detailRow}>
                        <strong>Event:</strong> {getTitle(e)}
                      </div>
                      <div className={classes.detailRow}>
                        <strong>Type:</strong> {text(e.type || getCategory(e))}
                      </div>
                      <div className={classes.detailRow}>
                        <strong>Action:</strong> {action}
                      </div>
                      <div className={classes.detailRow}>
                        <strong>Date:</strong> {dateTime(getDateValue(e))}
                      </div>
                      {actor && (
                        <div className={classes.detailRow}>
                          <strong>Performed by:</strong> {actor}
                        </div>
                      )}
                      {entityName && (
                        <div className={classes.detailRow}>
                          <strong>Entity:</strong> {entityName}
                        </div>
                      )}
                      <div className={classes.detailRow}>
                        <strong>Description:</strong>{' '}
                        {text(e.raw_description || e.description) || 'No additional details'}
                      </div>
                    </div>
                  )}
                </div>
                <div className={classes.right}>
                  <Chip className={classes.chip} size='small' label={action === 'sync' ? 'sync' : `app ${action}`} />
                  <div>
                    <button
                      type='button'
                      className={classes.detailsLink}
                      onClick={() => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
                    >
                      {expanded[id] ? 'Hide details' : 'View details'}
                    </button>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })
      )}
      <div className={classes.footer}>
        <TablePagination
          component='div'
          count={filteredEvents.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangePage={(_event, nextPage) => setPage(nextPage)}
          onChangeRowsPerPage={(e) => setRowsPerPage(Number(e.target.value))}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Paper>
  );
};

export default AppTimeline;
